// src/utils/seo.js
// Dynamic page title, meta tags, and structured data management

const SITE_NAME = 'OPHIDIA CLOUD';
const SITE_URL = 'https://ophidia.cloud';
const DEFAULT_DESC = 'OPU-1 Access Point.';
// CHANGED: add a default OG image constant
const DEFAULT_IMAGE = `${SITE_URL}/og-default.png`;

// ─── Low-level helpers ───────────────────────────────────────────

function setMetaTag(name, content, attr = 'name') {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

// CHANGED: helper to set or create <link rel="canonical">
function setCanonical(url) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

// CHANGED: generic JSON-LD injector with unique IDs so multiple
// schemas can coexist (site-schema, story-schema, collection-schema)
function setJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  if (!data) return;

  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

// ─── Page-level SEO ──────────────────────────────────────────────

// CHANGED: extended setPageMeta with canonical URL, og:image,
// og:site_name, twitter:card, and twitter:image
export function setPageMeta({
  title,
  description,
  url,
  type = 'website',
  image,
} = {}) {
  // Title
  document.title = title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — Archived Transmissions`;

  const desc = description || DEFAULT_DESC;
  const pageUrl = url || SITE_URL;
  const img = image || DEFAULT_IMAGE;

  // Meta description
  setMetaTag('description', desc);

  // CHANGED: canonical URL
  setCanonical(pageUrl);

  // Open Graph
  setMetaTag('og:site_name', SITE_NAME, 'property'); // CHANGED: added
  setMetaTag('og:title', title || SITE_NAME, 'property');
  setMetaTag('og:description', desc, 'property');
  setMetaTag('og:url', pageUrl, 'property');
  setMetaTag('og:type', type, 'property');
  setMetaTag('og:image', img, 'property'); // CHANGED: added

  // Twitter
  setMetaTag('twitter:card', 'summary_large_image'); // CHANGED: added
  setMetaTag('twitter:title', title || SITE_NAME);
  setMetaTag('twitter:description', desc);
  setMetaTag('twitter:image', img); // CHANGED: added
}

// ─── Story / Article schema ──────────────────────────────────────

// CHANGED: updated schema type from ShortStory to Article (much
// better Google support), fixed URL to use /archives/ path, added
// mainEntityOfPage, made tags safe against undefined
export function setStorySchema(story) {
  if (!story) {
    setJsonLd('story-schema', null);
    return;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: story.title,
    description: story.excerpt || story.title,
    // CHANGED: use /archives/ instead of /stories/
    url: `${SITE_URL}/archives/${story.slug}`,
    datePublished: story.publishedAt || story.createdAt,
    dateModified: story.updatedAt || story.publishedAt || story.createdAt,
    author: {
      '@type': 'Person',
      name: 'The God Circuit',
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    // CHANGED: added mainEntityOfPage for richer indexing
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/archives/${story.slug}`,
    },
    // CHANGED: guard against missing tags array
    keywords: story.tags && story.tags.length > 0
      ? story.tags.join(', ')
      : undefined,
  };

  setJsonLd('story-schema', schema);
}

// ─── Collection schema (archive listing page) ───────────────────

// CHANGED: entirely new — tells Google the archive page is a
// structured collection with links to every transcript
export function setCollectionSchema(stories) {
  if (!stories || stories.length === 0) {
    setJsonLd('collection-schema', null);
    return;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `Archived Transcripts — ${SITE_NAME}`,
    description:
      'Browse all archived transmissions and transcripts from The God Circuit.',
    url: `${SITE_URL}/archives`,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: stories.length,
      // Cap at 50 items to keep payload reasonable
      itemListElement: stories.slice(0, 50).map((s, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_URL}/archives/${s.slug}`,
        name: s.title,
      })),
    },
  };

  setJsonLd('collection-schema', schema);
}

// ─── Website-level schema ────────────────────────────────────────

// CHANGED: use setJsonLd helper so it doesn't duplicate on re-mount,
// removed non-standard 'genre' field
export function setSiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESC,
    author: {
      '@type': 'Person',
      name: 'The God Circuit',
    },
  };

  setJsonLd('site-schema', schema);
}